import { Badge, Button, Group, Modal, MultiSelect, Stack, Switch, Text, VisuallyHidden } from '@mantine/core';
import { IconMailForward } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppNotification } from '@feature/utils';

import { getTravelDeanMailRoutingOptions, useTravelDeanMailRoutingEdit } from '../model/TravelApprovalQuery';
import { TravelDeanMailRouting as Routing } from '../model/TravelApprovalTypes';

export function TravelDeanMailRouting() {
  const { t } = useTranslation();
  const { data, isError, isFetching, refetch } = useQuery(getTravelDeanMailRoutingOptions());
  const [opened, setOpened] = useState(false);

  if (isError && !data) {
    return (
      <Group gap="xs">
        <Text
          size="xs"
          c="red"
        >
          {t('travel.deanMailRouting.loadError')}
        </Text>
        <Button
          size="xs"
          variant="subtle"
          loading={isFetching}
          onClick={() => refetch()}
        >
          {t('pageError.retry')}
        </Button>
      </Group>
    );
  }

  if (!data) {
    return null;
  }

  const selectedCount = selectedIdsOf(data).length;

  return (
    <>
      <Button
        size="xs"
        variant="outline"
        leftSection={<IconMailForward size={14} />}
        rightSection={
          data.enabled && selectedCount > 0 ? (
            <>
              <Badge
                size="xs"
                circle
                aria-hidden
              >
                {selectedCount}
              </Badge>
              <VisuallyHidden>{t('travel.deanMailRouting.selectedCount', { count: selectedCount })}</VisuallyHidden>
            </>
          ) : undefined
        }
        onClick={() => setOpened(true)}
      >
        {t('travel.deanMailRouting.button')}
      </Button>
      {/* Mounting the modal on open starts every edit from what the server holds. */}
      {opened && (
        <TravelDeanMailRoutingModal
          routing={data}
          onClose={() => setOpened(false)}
        />
      )}
    </>
  );
}

const selectedIdsOf = (routing: Routing) => [
  ...new Set(routing.viceDeans.filter(viceDean => viceDean.selected).map(viceDean => String(viceDean.id))),
];

// A vice-dean shared by two faculties the dean heads arrives once per faculty, and MultiSelect rejects repeated values.
function viceDeanOptionsOf(routing: Routing) {
  const options = new Map<string, { name: string; faculties: string[] }>();
  routing.viceDeans.forEach(viceDean => {
    const option = options.get(String(viceDean.id)) ?? { name: viceDean.name, faculties: [] };
    option.faculties.push(viceDean.facultyName);
    options.set(String(viceDean.id), option);
  });
  return [...options].map(([value, option]) => ({ value, label: `${option.name} - ${option.faculties.join(', ')}` }));
}

function TravelDeanMailRoutingModal({ routing, onClose }: { routing: Routing; onClose: () => void }) {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const save = useTravelDeanMailRoutingEdit();
  const [enabled, setEnabled] = useState(routing.enabled);
  const [picked, setPicked] = useState(() => selectedIdsOf(routing));
  const options = viceDeanOptionsOf(routing);
  // A refused save refetches the list, and a vice-dean who no longer serves must drop out of the pick with it.
  const selection = picked.filter(id => options.some(option => option.value === id));
  const noViceDeans = routing.viceDeans.length === 0;
  const selectionMissing = enabled && selection.length === 0;
  const close = () => {
    if (!save.isPending) {
      onClose();
    }
  };

  const submit = (request: { enabled: boolean; viceDeanIds: string[] }) =>
    save.mutate(
      { enabled: request.enabled, viceDeanIds: request.viceDeanIds.map(Number) },
      {
        onSuccess: () => {
          notification.success();
          onClose();
        },
      },
    );

  return (
    <Modal
      opened
      centered
      onClose={close}
      closeOnEscape={!save.isPending}
      closeOnClickOutside={!save.isPending}
      withCloseButton={!save.isPending}
      title={t('travel.deanMailRouting.title')}
      size="lg"
    >
      {noViceDeans ? (
        <Stack gap="md">
          <Stack gap={4}>
            <Text fw={600}>{t('travel.deanMailRouting.emptyTitle')}</Text>
            <Text size="sm">{t('travel.deanMailRouting.emptyText')}</Text>
            {routing.enabled && (
              <Text
                size="sm"
                c="dimmed"
              >
                {t('travel.deanMailRouting.emptyFallback')}
              </Text>
            )}
          </Stack>
          <Group justify="flex-end">
            {routing.enabled && (
              <Button
                variant="outline"
                loading={save.isPending}
                onClick={() => submit({ enabled: false, viceDeanIds: [] })}
              >
                {t('travel.deanMailRouting.turnOff')}
              </Button>
            )}
            <Button
              variant="default"
              disabled={save.isPending}
              onClick={close}
            >
              {t('button.close')}
            </Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap="md">
          <Text size="sm">{t('travel.deanMailRouting.explanation')}</Text>
          <Switch
            label={t('travel.deanMailRouting.enabled')}
            checked={enabled}
            disabled={save.isPending}
            onChange={event => setEnabled(event.currentTarget.checked)}
          />
          {enabled && (
            <MultiSelect
              label={t('travel.deanMailRouting.viceDeans')}
              data={options}
              value={selection}
              onChange={setPicked}
              disabled={save.isPending}
              error={selectionMissing ? t('travel.deanMailRouting.selectionRequired') : undefined}
              searchable
            />
          )}
          <Group justify="flex-end">
            <Button
              variant="default"
              disabled={save.isPending}
              onClick={close}
            >
              {t('button.cancel')}
            </Button>
            <Button
              // Turning it off resends the saved selection so the server keeps it for the next time.
              onClick={() => submit({ enabled, viceDeanIds: enabled ? selection : selectedIdsOf(routing) })}
              loading={save.isPending}
              disabled={selectionMissing}
            >
              {t('button.save')}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
